import React, { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { FaAngleRight } from 'react-icons/fa'
import { TrainerCard } from './TopTrainersSection'

// Number of trainers surfaced above the fold. Enough to read as a choice set
// without pushing the price anchor and CTA below the fold on a 390px viewport.
//
// Exported because PersonalTrainer slices the SAME list past this point for the
// Meet Our Trainers grid - the hero takes 0..N, the grid takes N.. - so the two
// sections never show the same trainer twice. Keep it the single source of truth.
export const HERO_TRAINER_COUNT = 6

// Below this the strip reads as a broken widget rather than a choice set, so we
// drop it and let the hero fall back to headline + price anchor + CTA. The page
// is statically generated with a 3-day revalidate, so a roster fetch that fails
// at build time would otherwise ship a near-empty fold for the whole window.
// Same reasoning as the trainerCount fallback in pages/sg/personal-trainer.
const MIN_TRAINERS_FOR_STRIP = 3

/**
 * The above-the-fold trainer choice-set.
 *
 * Search intent for "personal trainer Singapore" is investigational - the SERP is
 * listicles and directories, not single-brand booking pages - so the hero leads
 * with who you would actually train with rather than a package price grid. The
 * packages moved one fold down (see PersonalTrainerHead).
 *
 * Cards are the shared TrainerCard in its "hero" variant so this strip and the
 * Meet Our Trainers grid below cannot drift apart.
 */
export const HeroTrainerStrip = ({ spList, trainerCount, seeAllHref, seeAllLabel }) => {
    const scrollRef = useRef(null)
    const [showArrow, setShowArrow] = useState(false)

    const trainers = Array.isArray(spList) ? spList.slice(0, HERO_TRAINER_COUNT) : []

    const updateScrollState = useCallback(() => {
        const el = scrollRef.current
        if (!el) return
        const { scrollLeft, scrollWidth, clientWidth } = el
        setShowArrow(scrollLeft + clientWidth < scrollWidth - 10)
    }, [])

    useEffect(() => {
        const el = scrollRef.current
        if (!el) return
        updateScrollState()
        el.addEventListener('scroll', updateScrollState, { passive: true })
        window.addEventListener('resize', updateScrollState)
        return () => {
            el.removeEventListener('scroll', updateScrollState)
            window.removeEventListener('resize', updateScrollState)
        }
    }, [updateScrollState, trainers.length])

    const scrollToNext = () => {
        const el = scrollRef.current
        if (!el) return
        el.scrollBy({ left: Math.round(el.clientWidth * 0.85), behavior: 'smooth' })
    }

    if (trainers.length < MIN_TRAINERS_FOR_STRIP) return null

    return (
        <div className='relative'>
            {/* Horizontal snap-scroll on mobile; wraps into rows of three at sm+. */}
            <div
                ref={scrollRef}
                className='flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-1
                    sm:flex-wrap sm:justify-center sm:overflow-visible'
            >
                {trainers.map((sp, i) => <TrainerCard key={sp.handleName || i} sp={sp} variant='hero' />)}
            </div>

            {showArrow &&
                <button
                    type='button'
                    onClick={scrollToNext}
                    aria-label='Show more trainers'
                    className='sm:hidden absolute right-0 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-slate-900/85 backdrop-blur-sm border border-white/20 text-white flex items-center justify-center shadow-lg'
                >
                    <FaAngleRight />
                </button>
            }

            {/* The hub knows the full roster size and links to the island-wide
                directory. A locality page does not know the roster size and has its
                own full list directly below, so it passes its own label - or omits
                the link entirely - rather than claiming a count it cannot back up. */}
            <div className='flex justify-end sm:justify-center pt-3'>
                <Link
                    href={seeAllHref || '/sg/personal-trainer#trainer-map'}
                    prefetch={false}
                    className='text-sm font-semibold text-orange-400 hover:text-orange-300 transition-colors'
                >
                    {seeAllLabel ? (
                        <span>{seeAllLabel} &rarr;</span>
                    ) : (
                        <>
                            <span className='hidden md:block'>
                                See all {trainerCount} certified personal trainers in Singapore &rarr;
                            </span>
                            <span className='md:hidden'>
                                See all {trainerCount} trainers &rarr;
                            </span>
                        </>
                    )}
                </Link>
            </div>
        </div>
    )
}
